import { ref } from 'vue';
import { useTheme } from 'vuetify';

export const THEME_CHOICES = ['system', 'light', 'dark'] as const;
export type ThemeChoice = (typeof THEME_CHOICES)[number];

const STORAGE_KEY = 'stadtpuls.theme';

function stored(): ThemeChoice {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if ((THEME_CHOICES as readonly string[]).includes(value ?? '')) return value as ThemeChoice;
  } catch {
    // Storage unavailable; follow the system.
  }
  return 'system';
}

/**
 * The operator's light or dark choice, remembered per browser.
 *
 * A convenience, so it lives in localStorage and every access tolerates
 * storage being unavailable. "system" is a real choice and not the absence of
 * one: it keeps following the operating system when that switches at dusk.
 */
export function useThemePreference() {
  const theme = useTheme();
  const choice = ref<ThemeChoice>(stored());

  function apply(next: ThemeChoice): void {
    choice.value = next;
    void theme.change(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not remembered, still applied.
    }
  }

  apply(choice.value);
  return { choice, apply };
}
